import React, { useState, useEffect } from 'react';
import {
  Shield,
  ShieldAlert,
  ClipboardList,
  SlidersHorizontal,
  LayoutDashboard,
  Network,
  Check,
  ArrowLeft,
  ArrowRight,
  Save,
} from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import './styles/PlatformConfiguration.css';
import { getModules, assignOrganizationModules, Module } from '../../services/moduleActivationService';
import { toast } from 'react-toastify';

interface ModuleActivationProps {
  organizationId: string;
  nextStep: () => void;
  previousStep: () => void;
}

interface ModuleItem {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  badge?: string;
}

interface ModuleActivationFormValues {
  selectedIds: string[];
}

// Local presentation config keyed by module title (lowercased) — real API ids are UUIDs,
// so we can't key on m.id. Extend as new module titles are added.
const MODULE_PRESENTATION: Record<string, { icon: React.ElementType; iconBg: string; iconColor: string; badge?: string }> = {
  compliance: { icon: Shield, iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600' },
  'risk management': { icon: ShieldAlert, iconBg: 'bg-rose-100', iconColor: 'text-rose-500' },
  governance: { icon: ClipboardList, iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
  controls: { icon: SlidersHorizontal, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
};

const getPresentation = (title: string) => MODULE_PRESENTATION[title.trim().toLowerCase()] || DEFAULT_PRESENTATION;

const DEFAULT_PRESENTATION = { icon: Shield, iconBg: 'bg-gray-100', iconColor: 'text-gray-600' };

const dashboards: ModuleItem[] = [
  {
    id: 'executive-dashboard',
    title: 'Executive Dashboard',
    description: 'Organization-wide KPIs, compliance health, controls, approvals, and activity',
    icon: LayoutDashboard,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
  },
  {
    id: 'group-dashboard',
    title: 'Group Dashboard',
    description: 'Consolidated view across all subsidiaries — scores, escalations, and health per company',
    icon: Network,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    badge: 'Holding group only',
  },
];

const moduleActivationValidationSchema = Yup.object({
  selectedIds: Yup.array().min(1, 'Select at least one module to continue'),
});

const ModuleActivation: React.FC<ModuleActivationProps> = ({ organizationId, nextStep, previousStep }) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [enabledModules, setEnabledModules] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [enabledDashboards, setEnabledDashboards] = useState<Record<string, boolean>>({
    'executive-dashboard': false,
    'group-dashboard': false,
  });

  const formik = useFormik<ModuleActivationFormValues>({
    initialValues: {
      selectedIds: [],
    },
    validationSchema: moduleActivationValidationSchema,
    onSubmit: () => {
      // Validation-only — actual save/navigation is handled in handleNext.
    },
  });

  // Keep formik's selectedIds in sync with enabled modules only — dashboards are optional
  // and don't count toward the "at least one selected" requirement.
  useEffect(() => {
    const moduleIds = Object.keys(enabledModules).filter((id) => enabledModules[id]);
    formik.setFieldValue('selectedIds', moduleIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledModules]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        const data = await getModules();
        setModules(data);

        const initialEnabled: Record<string, boolean> = {};
        data.forEach((m: Module) => {
          initialEnabled[m.id] = false;
        });
        setEnabledModules(initialEnabled);
      } catch (err) {
        setError('Failed to load modules');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchModules();
  }, []);

  const toggleModule = (id: string) => {
    setEnabledModules((prev) => ({ ...prev, [id]: !prev[id] }));
    formik.setFieldTouched('selectedIds', true);
  };

  const toggleDashboard = (id: string) => {
    setEnabledDashboards((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getSelectedModuleIds = () => Object.keys(enabledModules).filter((id) => enabledModules[id]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);

      const payload = {
        organizationId,
        moduleIds: getSelectedModuleIds(),
      };

      const result = await assignOrganizationModules(payload);
      toast.success('Modules saved successfully');
    } catch (err) {
      setSaveError('Failed to save module selection');
      toast.error('Failed to save module selection');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const errors = await formik.validateForm();
    formik.setFieldTouched('selectedIds', true);

    if (errors.selectedIds) {
      toast.error(errors.selectedIds as string);
      return;
    }

    await handleSave();
    nextStep();
  };

  const renderRow = (item: ModuleItem, isEnabled: boolean, onToggle: (id: string) => void) => {
    const Icon = item.icon;
    return (
      <div
        key={item.id}
        onClick={() => onToggle(item.id)}
        className={`flex items-center justify-between rounded-xl border px-4 py-3.5 cursor-pointer transition-colors
          ${isEnabled ? 'bg-emerald-50/70 border-emerald-100' : 'bg-white border-gray-200'}
        `}
      >
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${item.iconBg}`}>
            <Icon className={`h-5 w-5 ${item.iconColor}`} />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-gray-900">{item.title}</p>
              {item.badge && (
                <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[11px] font-medium text-fuchsia-600">{item.badge}</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
          </div>
        </div>

        <div
          className={`flex h-6 w-6 items-center justify-center rounded-full
            ${isEnabled ? 'bg-emerald-500' : 'bg-gray-200'}
          `}
        >
          {isEnabled && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
        </div>
      </div>
    );
  };

  return (
    <div className="module-activation-wrapper">
      <h2 className="text-lg font-semibold text-gray-900">Module Activation</h2>
      <p className="text-sm text-gray-500 mt-1">Choose which modules and dashboards to activate</p>
      <p className="text-xs text-gray-400 mt-2">
        Select which modules to activate. Only enabled modules will appear in navigation, role setup, and module configuration.
      </p>

      {loading && <p className="text-sm text-gray-500 mt-4">Loading modules...</p>}
      {error && <p className="text-sm text-red-500 mt-4">{error}</p>}

      {!loading && !error && (
        <div className="mt-6 flex flex-col gap-3">
          {modules.map((m) =>
            renderRow(
              {
                id: m.id,
                title: m.title,
                description: m.description || '',
                icon: getPresentation(m.title).icon,
                iconBg: getPresentation(m.title).iconBg,
                iconColor: getPresentation(m.title).iconColor,
                badge: getPresentation(m.title).badge,
              },
              enabledModules[m.id],
              toggleModule,
            ),
          )}
        </div>
      )}

      {formik.touched.selectedIds && formik.errors.selectedIds && (
        <p className="text-sm mt-3" style={{ color: '#dc2626' }}>
          {formik.errors.selectedIds as string}
        </p>
      )}

      <div className="mt-8">
        <h3 className="text-sm font-semibold text-gray-900">Dashboards</h3>
        <p className="text-xs text-gray-400 mt-1">Choose which dashboards to enable.</p>

        <div className="mt-4 flex flex-col gap-3">{dashboards.map((d) => renderRow(d, enabledDashboards[d.id], toggleDashboard))}</div>
      </div>

      {saveError && <p className="text-sm text-red-500 mt-4">{saveError}</p>}

      {/* Footer */}
      <div className="organization-footer">
        {/* Back */}
        <button type="button" onClick={previousStep} className="organization-back-btn">
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        <div className="organization-footer-right">
          {/* Save */}
          <button type="button" onClick={handleSave} disabled={saving} className="organization-save-btn">
            <Save size={16} />
            <span>{saving ? 'Saving...' : 'Save & Edit'}</span>
          </button>

          {/* Next */}
          <button type="button" onClick={handleNext} disabled={saving} className="organization-next-btn">
            <span>Next</span>
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModuleActivation;
